import React, { useState, useMemo, useRef, useEffect } from 'react';
import classNames from 'classnames';
import styles from './PaginationControls.module.scss';

// ============================================================================
// TYPES
// ============================================================================
interface PaginationControlsProps {
    currentPage: number;
    totalPages: number;
    itemsPerPage: number;
    onPageChange: (page: number) => void;
    onItemsPerPageChange: (items: number) => void;
    showOptions?: number[];
    className?: string;
    loading?: boolean;
}

const DOTS = '...';

// ============================================================================
// HELPERS
// ============================================================================
const range = (start: number, end: number): number[] =>
    Array.from({ length: end - start + 1 }, (_, i) => start + i);

const getPageNumbers = (
    currentPage: number,
    totalPages: number,
    siblingCount = 1
): (number | string)[] => {
    const totalPageNumbers = siblingCount * 2 + 5;

    if (totalPages <= 0) {
        return [1];
    }

    if (totalPageNumbers >= totalPages) {
        return range(1, totalPages);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;

    if (!shouldShowLeftDots && shouldShowRightDots) {
        const leftItemCount = 3 + siblingCount * 2;
        return [...range(1, leftItemCount), DOTS, lastPageIndex];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
        const rightItemCount = 3 + siblingCount * 2;
        return [firstPageIndex, DOTS, ...range(totalPages - rightItemCount + 1, totalPages)];
    }

    return [firstPageIndex, DOTS, ...range(leftSiblingIndex, rightSiblingIndex), DOTS, lastPageIndex];
};

// ============================================================================
// PAGINATION CONTROLS COMPONENT
// ============================================================================
const PaginationControls: React.FC<PaginationControlsProps> = ({
    currentPage,
    totalPages,
    itemsPerPage,
    onPageChange,
    onItemsPerPageChange,
    showOptions = [10, 25, 50, 100],
    className = '',
    loading = false,
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const pageNumbers = useMemo(
        () => getPageNumbers(currentPage, totalPages, 1),
        [currentPage, totalPages]
    );

    const handleShowChange = (items: number) => {
        onItemsPerPageChange(items);
        setIsDropdownOpen(false);
    };

    const handlePageClick = (page: number | string) => {
        if (typeof page === 'number' && page !== currentPage) {
            onPageChange(page);
        }
    };

    return (
        <div className={classNames(styles.paginationControls, className)}>
            {/* Show Selector */}
            <div className={styles.showSelectorWrapper}>
                <span className={styles.showLabel}>Show</span>
                <div className={styles.dropdownContainer} ref={dropdownRef}>
                    <button
                        type="button"
                        className={classNames(styles.glassDropButton, styles.dropdownToggle)}
                        onClick={() => setIsDropdownOpen((prev) => !prev)}
                        disabled={loading}
                        aria-label="Items per page"
                        aria-haspopup="listbox"
                        aria-expanded={isDropdownOpen}
                    >
                        {itemsPerPage}
                        <svg
                            className={classNames(styles.dropdownIcon, { [styles.open]: isDropdownOpen })}
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M4 6L8 10L12 6"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>

                    {isDropdownOpen && (
                        <div className={styles.dropdownMenu} role="listbox">
                            {showOptions.map((option) => (
                                <button
                                    key={option}
                                    type="button"
                                    className={classNames(styles.dropdownItem, {
                                        [styles.active]: itemsPerPage === option,
                                    })}
                                    onClick={() => handleShowChange(option)}
                                    role="option"
                                    aria-selected={itemsPerPage === option}
                                >
                                    {option}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Pagination */}
            <div className={styles.paginationWrapper}>
                {/* Previous Button */}
                <button
                    type="button"
                    className={classNames(styles.paginationNavBtn)}
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage <= 1 || loading}
                    aria-label="Previous page"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M15 19l-7-7 7-7"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>

                {/* Page Numbers */}
                <div className={styles.pageNumbers}>
                    {pageNumbers.map((page, index) =>
                        page === DOTS ? (
                            <span key={`dots-${index}`} className={styles.ellipsis}>
                                {DOTS}
                            </span>
                        ) : (
                            <button
                                key={page}
                                type="button"
                                className={classNames(styles.glassButton, styles.pageBtn, {
                                    [styles.active]: currentPage === page,
                                })}
                                onClick={() => handlePageClick(page)}
                                disabled={loading}
                                aria-current={currentPage === page ? 'page' : undefined}
                            >
                                {page}
                            </button>
                        )
                    )}
                </div>

                {/* Next Button */}
                <button
                    type="button"
                    className={classNames(styles.paginationNavBtn)}
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages || loading}
                    aria-label="Next page"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M9 5l7 7-7 7"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export { PaginationControls };
export default PaginationControls;